import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { connectWS, disconnectWS } from '@/lib/ws';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [cartCount, setCartCount] = useState(0);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWishlist = useCallback(async () => {
    try {
      const { data } = await api.get('/wishlist/ids');
      setWishlistIds(new Set(data));
    } catch {
      setWishlistIds(new Set());
    }
  }, []);

  const fetchCartCount = useCallback(async () => {
    try {
      const { data } = await api.get('/cart/count');
      setCartCount(data.count || 0);
    } catch { setCartCount(0); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => {
    if (user) {
      fetchWishlist();
      fetchCartCount();
      connectWS();
    }
  }, [user, fetchWishlist, fetchCartCount]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post('/auth/register', { email, password, name });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(false);
    setWishlistIds(new Set());
    setCartCount(0);
    disconnectWS();
  };

  const toggleWishlist = async (itemId) => {
    if (!user) return false;
    try {
      if (wishlistIds.has(itemId)) {
        await api.delete(`/wishlist/${itemId}`);
        setWishlistIds(prev => { const n = new Set(prev); n.delete(itemId); return n; });
      } else {
        await api.post(`/wishlist/${itemId}`);
        setWishlistIds(prev => new Set(prev).add(itemId));
      }
      return true;
    } catch { return false; }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, wishlistIds, toggleWishlist, fetchWishlist, cartCount, fetchCartCount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
